import { ref } from 'vue'

export type SortOrder = 'asc' | 'desc'

export interface TableSort {
  key: string
  order: SortOrder
}

export interface TableOptions {
  sortBy?: TableSort[]
  page?: number
  itemsPerPage?: number
}

interface UseServerTableOptionsParams {
  initialPage?: number
  initialItemsPerPage?: number
  initialSort?: TableSort | null
}

interface UseServerTableOptionsResult {
  page: ReturnType<typeof ref<number>>
  lastFetchedPage: ReturnType<typeof ref<number>>
  lastFetchedItemsPerPage: ReturnType<typeof ref<number>>
  currentSort: ReturnType<typeof ref<TableSort | null>>
  isSamePageRequest: (nextPage: number, nextItemsPerPage: number) => boolean
  markFetched: (nextPage: number, nextItemsPerPage: number) => void
  resetToFirstPage: () => void
  updateSort: (sort: TableSort | null) => void
  toggleSort: (columnKey: string) => TableSort
}

export function useServerTableOptions(
  params: UseServerTableOptionsParams = {},
): UseServerTableOptionsResult {
  const initialPage = params.initialPage ?? 1
  const initialItemsPerPage = params.initialItemsPerPage ?? 10

  const page = ref(initialPage)
  const lastFetchedPage = ref(initialPage)
  const lastFetchedItemsPerPage = ref(initialItemsPerPage)
  const currentSort = ref<TableSort | null>(params.initialSort ?? null)

  const isSamePageRequest = (nextPage: number, nextItemsPerPage: number) => {
    return (
      nextPage === lastFetchedPage.value
      && nextItemsPerPage === lastFetchedItemsPerPage.value
    )
  }

  const markFetched = (nextPage: number, nextItemsPerPage: number) => {
    lastFetchedPage.value = nextPage
    lastFetchedItemsPerPage.value = nextItemsPerPage
  }

  const resetToFirstPage = () => {
    page.value = 1
  }

  const updateSort = (sort: TableSort | null) => {
    currentSort.value = sort
  }

  const toggleSort = (columnKey: string): TableSort => {
    if (!currentSort.value || currentSort.value.key !== columnKey) {
      const next = { key: columnKey, order: 'asc' as const }

      currentSort.value = next

      return next
    }

    const next = {
      key: columnKey,
      order: currentSort.value.order === 'asc' ? 'desc' : 'asc',
    } as const

    currentSort.value = next

    return next
  }

  return {
    page,
    lastFetchedPage,
    lastFetchedItemsPerPage,
    currentSort,
    isSamePageRequest,
    markFetched,
    resetToFirstPage,
    updateSort,
    toggleSort,
  }
}
